import React, { useState, FormEvent, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './App.css';
import logo from './logo.svg';
import MovieDetail from './MovieDetail';
import { signInWithPopup, User, GoogleAuthProvider, FacebookAuthProvider, OAuthProvider } from 'firebase/auth';
import { auth } from './firebase';

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY; // TMDb API key from environment variables

export interface Movie {
  id: number;
  title: string;
  release_date: string;
  poster_path: string | null;
  backdrop_path: string | null;
  overview: string;
  vote_average: number;
}

export interface MovieCategory {
  title: string;
  endpoint: string;
}

const movieCategories: MovieCategory[] = [
  { title: "Now Playing", endpoint: "movie/now_playing" },
  { title: "Popular", endpoint: "movie/popular" },
  { title: "Top Rated", endpoint: "movie/top_rated" },
  { title: "Upcoming", endpoint: "movie/upcoming" },
  { title: "Action", endpoint: "discover/movie?with_genres=28" },
  { title: "Adventure", endpoint: "discover/movie?with_genres=12" },
  { title: "Animation", endpoint: "discover/movie?with_genres=16" },
  { title: "Comedy", endpoint: "discover/movie?with_genres=35" },
  { title: "Crime", endpoint: "discover/movie?with_genres=80" },
  { title: "Documentary", endpoint: "discover/movie?with_genres=99" },
  { title: "Drama", endpoint: "discover/movie?with_genres=18" },
  { title: "Family", endpoint: "discover/movie?with_genres=10751" },
  { title: "Fantasy", endpoint: "discover/movie?with_genres=14" },
  { title: "History", endpoint: "discover/movie?with_genres=36" },
  { title: "Horror", endpoint: "discover/movie?with_genres=27" },
  { title: "Music", endpoint: "discover/movie?with_genres=10402" },
  { title: "Mystery", endpoint: "discover/movie?with_genres=9648" },
  { title: "Romance", endpoint: "discover/movie?with_genres=10749" },
  { title: "Science Fiction", endpoint: "discover/movie?with_genres=878" },
  { title: "TV Movie", endpoint: "discover/movie?with_genres=10770" },
  { title: "Thriller", endpoint: "discover/movie?with_genres=53" },
  { title: "War", endpoint: "discover/movie?with_genres=10752" },
  { title: "Western", endpoint: "discover/movie?with_genres=37" },
  { title: "Oscar Winning", endpoint: "discover/movie?with_keywords=1255" },
  { title: "Superhero", endpoint: "discover/movie?with_keywords=9715" },
  { title: "Anime", endpoint: "discover/movie?with_keywords=210024" },
  { title: "Film Noir", endpoint: "discover/movie?with_keywords=188" },
  { title: "Heist", endpoint: "discover/movie?with_keywords=9663" },
  { title: "Spy", endpoint: "discover/movie?with_keywords=10068" },
  { title: "Disaster", endpoint: "discover/movie?with_keywords=163" },
  { title: "Martial Arts", endpoint: "discover/movie?with_keywords=4344" },
  { title: "Zombie", endpoint: "discover/movie?with_keywords=12377" },
  { title: "Vampire", endpoint: "discover/movie?with_keywords=10469" },
  { title: "Werewolf", endpoint: "discover/movie?with_keywords=12564" },
  { title: "Time Travel", endpoint: "discover/movie?with_keywords=4379" },
  { title: "Dystopian", endpoint: "discover/movie?with_keywords=12565" },
  { title: "Post-Apocalyptic", endpoint: "discover/movie?with_keywords=4458" },
  { title: "Alien Invasion", endpoint: "discover/movie?with_keywords=9951" },
  { title: "Cyberpunk", endpoint: "discover/movie?with_keywords=769" },
  { title: "Steampunk", endpoint: "discover/movie?with_keywords=33893" },
  { title: "Psychological Thriller", endpoint: "discover/movie?with_keywords=11249" },
  { title: "Cult Classic", endpoint: "discover/movie?with_keywords=34" },
  { title: "Based on a True Story", endpoint: "discover/movie?with_keywords=9672" },
  { title: "Based on a Book", endpoint: "discover/movie?with_keywords=818" },
  { title: "Coming of Age", endpoint: "discover/movie?with_keywords=10683" },
  { title: "Sports", endpoint: "discover/movie?with_keywords=9799" },
  { title: "Musical", endpoint: "discover/movie?with_keywords=4344" },
  { title: "Political", endpoint: "discover/movie?with_keywords=6054" },
  { title: "Courtroom Drama", endpoint: "discover/movie?with_keywords=3329" },
  { title: "Movies with Plot Twist", endpoint: "discover/movie?with_keywords=177" },
  { title: "Hindi", endpoint: "discover/movie?with_original_language=hi" },
];

interface CarouselState {
  currentIndex: number;
  transition: string;
}

interface MovieRow {
  title: string;
  movies: Movie[];
  page: number;
  hasMore: boolean;
}

function App() {
  const [query, setQuery] = useState('');
  const [isShowingSearchResults, setIsShowingSearchResults] = useState(false);
  const [featuredMovies, setFeaturedMovies] = useState<Movie[]>([]);
  const [searchResults, setSearchResults] = useState<Movie[]>([]);
  const [movieRows, setMovieRows] = useState<MovieRow[]>([]);
  const [selectedTrailer, setSelectedTrailer] = useState<string | null>(null);
  const [carouselState, setCarouselState] = useState<CarouselState>({
    currentIndex: 0,
    transition: 'fade'
  });
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [watchProviders, setWatchProviders] = useState<any>(null);
  const [explodingIndex, setExplodingIndex] = useState<number | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [visitedMovies, setVisitedMovies] = useState<Movie[]>(() => {
    const storedVisitedMovies = localStorage.getItem('visitedMovies');
    return storedVisitedMovies ? JSON.parse(storedVisitedMovies) : [];
  });
  const [currentView, setCurrentView] = useState<'home' | 'discover'>('home');
  const [discoverMovies, setDiscoverMovies] = useState<Movie[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const transitions = ['fade', 'slide-left', 'slide-right', 'slide-up', 'slide-down', 'zoom-in', 'zoom-out'];
  const observerRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
    });

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      unsubscribe();
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const storedVisitedMovies = localStorage.getItem('visitedMovies');
    if (storedVisitedMovies) {
      setVisitedMovies(JSON.parse(storedVisitedMovies));
    }
  }, []);

  const handleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // Sign-in successful, no alert
    } catch (error) {
      console.error('Error signing in with Google:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      // Sign-out successful, no alert
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
      // You might want to show an error message to the user here
    }
  };

  const fetchMoviesForCategory = async (category: string, page: number) => {
    try {
      let movies;
      if (category === "Hindi") {
        movies = await fetchHindiMovies(page);
      } else {
        const endpoint = movieCategories.find(cat => cat.title === category)?.endpoint;
        if (!endpoint) return [];
        movies = await fetchMovies(endpoint, page);
      }
      return movies;
    } catch (error) {
      console.error(`Error fetching ${category} movies:`, error);
      return [];
    }
  };

  const fetchAllMovies = useCallback(async () => {
    try {
      const initialMovieRows = await Promise.all(
        movieCategories.map(async (category) => {
          const movies = await fetchMoviesForCategory(category.title, 1);
          return { 
            title: category.title, 
            movies, 
            page: 1, 
            hasMore: movies.length === 20 // Assuming 20 is the page size
          };
        })
      );
      
      const nonEmptyCategories = initialMovieRows.filter(row => row.movies.length > 0);
      
      // Add "Last visited" category at the beginning
      setMovieRows([
        { title: "Last visited", movies: visitedMovies, page: 1, hasMore: false },
        ...nonEmptyCategories
      ]);
      
      // Use all movies for featured movies
      const allMovies = nonEmptyCategories.flatMap(row => row.movies);
      const shuffledMovies = allMovies.sort(() => Math.random() - 0.5);
      setFeaturedMovies(shuffledMovies);
    } catch (error) {
      console.error('Error fetching all movies:', error);
    }
  }, [visitedMovies]);

  const loadMoreMovies = useCallback(async (index: number) => {
    const row = movieRows[index];
    if (!row.hasMore) return;

    const newMovies = await fetchMoviesForCategory(row.title, row.page + 1);
    
    setMovieRows(prevRows => {
      const updatedRows = [...prevRows];
      updatedRows[index] = {
        ...row,
        movies: [...row.movies, ...newMovies],
        page: row.page + 1,
        hasMore: newMovies.length === 20 // Assuming 20 is the page size
      };
      return updatedRows;
    });
  }, [movieRows]);

  useEffect(() => {
    // Check the URL on initial load
    const searchParams = new URLSearchParams(window.location.search);
    const initialQuery = searchParams.get('q');
    if (initialQuery) {
      setQuery(initialQuery);
      performSearch(initialQuery);
    } else {
      fetchAllMovies();
    }

    // Add event listener for popstate (browser back/forward)
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const changeCarouselSlide = useCallback((direction: 'next' | 'prev') => {
    setCarouselState(prevState => {
      const newIndex = direction === 'next'
        ? (prevState.currentIndex + 1) % featuredMovies.length
        : (prevState.currentIndex - 1 + featuredMovies.length) % featuredMovies.length;
      
      const newTransition = transitions[Math.floor(Math.random() * transitions.length)];
      
      return {
        currentIndex: newIndex,
        transition: newTransition
      };
    });
  }, [featuredMovies.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      changeCarouselSlide('next');
    }, 10000); // Change every 10 seconds

    return () => clearInterval(interval);
  }, [changeCarouselSlide]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        changeCarouselSlide('prev');
      } else if (event.key === 'ArrowRight') {
        changeCarouselSlide('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [changeCarouselSlide]);

  useEffect(() => {
    fetchAllMovies();
  }, [fetchAllMovies, visitedMovies]);

  useEffect(() => {
    const observers = movieRows.map((_, index) => {
      return new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreMovies(index);
          }
        },
        { threshold: 1.0 }
      );
    });

    observerRefs.current.forEach((ref, index) => {
      if (ref) observers[index].observe(ref);
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [movieRows, loadMoreMovies]);

  const handlePopState = (event: PopStateEvent) => {
    const searchParams = new URLSearchParams(window.location.search);
    const query = searchParams.get('q');
    if (query) {
      setQuery(query);
      performSearch(query);
    } else {
      resetSearch();
    }
  };

  const fetchMovies = async (endpoint: string, page: number, params?: { query?: string }): Promise<Movie[]> => {
    try {
      const response = await axios.get<{ results: Movie[] }>(
        `https://api.themoviedb.org/3/${endpoint}`, {
          params: {
            api_key: TMDB_API_KEY,
            language: 'en-US',
            page,
            ...params
          }
        }
      );
      return response.data.results.filter(movie => movie.poster_path && movie.backdrop_path);
    } catch (error) {
      console.error(`Error fetching movies for ${endpoint}:`, error);
      return [];
    }
  };

  const fetchHindiMovies = async (page: number): Promise<Movie[]> => {
    try {
      const response = await axios.get<{ results: Movie[] }>(
        `https://api.themoviedb.org/3/discover/movie`, {
          params: {
            api_key: TMDB_API_KEY,
            language: 'hi-IN', // Hindi language
            with_original_language: 'hi', // Ensure movies are originally in Hindi
            page
          }
        }
      );
      return response.data.results.filter(movie => movie.poster_path && movie.backdrop_path);
    } catch (error) {
      console.error('Error fetching Hindi movies:', error);
      return [];
    }
  };

  const performSearch = async (searchQuery: string) => {
    setIsShowingSearchResults(true);
    try {
      const movies = await fetchMovies(`search/movie`, 1, { query: searchQuery });
      setSearchResults(movies);
      setMovieRows([{ title: "Search Results", movies, page: 1, hasMore: movies.length === 20 }]);
    } catch (error) {
      console.error('Error searching movies:', error);
    }
  };

  const handleLogoClick = () => {
    resetSearch();
    window.history.pushState(null, '', '/');
  };

  const searchMovies = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      window.history.pushState(null, '', `?q=${encodeURIComponent(query)}`);
      performSearch(query);
    }
  };

  const resetSearch = () => {
    setIsShowingSearchResults(false);
    setQuery('');
    setSearchResults([]);
    fetchAllMovies();
  };

  const playTrailer = async (movie: Movie) => {
    setSelectedMovie(null); // Close movie detail if open
    try {
      const response = await axios.get(
        `https://api.themoviedb.org/3/movie/${movie.id}/videos?api_key=${TMDB_API_KEY}`
      );
      const videos = response.data.results;
      const trailer = videos.find((video: any) => video.type === 'Trailer' && video.site === 'YouTube');
      if (trailer) {
        setSelectedTrailer(trailer.key);
      } else {
        console.error('No trailer found');
      }
    } catch (error) {
      console.error('Error fetching trailer:', error);
    }
  };

  const goToPreviousMovie = () => changeCarouselSlide('prev');
  const goToNextMovie = () => changeCarouselSlide('next');

  const openMovieDetail = async (movie: Movie) => {
    try {
      const [movieResponse, watchProvidersResponse] = await Promise.all([
        axios.get(`https://api.themoviedb.org/3/movie/${movie.id}?api_key=${TMDB_API_KEY}`),
        axios.get(`https://api.themoviedb.org/3/movie/${movie.id}/watch/providers?api_key=${TMDB_API_KEY}`)
      ]);

      setSelectedMovie(movieResponse.data);
      setWatchProviders(watchProvidersResponse.data);
      addToVisitedMovies(movie);
    } catch (error) {
      console.error('Error fetching movie details:', error);
    }
  };

  const closeMovieDetail = () => {
    setSelectedMovie(null);
    setWatchProviders(null);
  };

  const handleDoubleClick = (index: number) => {
    setExplodingIndex(index);
    setTimeout(() => setExplodingIndex(null), 1000); // Reset after animation
  };

  const addToVisitedMovies = useCallback((movie: Movie) => {
    setVisitedMovies(prevVisited => {
      const newVisited = [movie, ...prevVisited.filter(m => m.id !== movie.id)].slice(0, 10);
      localStorage.setItem('visitedMovies', JSON.stringify(newVisited));
      return newVisited;
    });
  }, []);

  const clearVisitedMovies = useCallback(() => {
    setVisitedMovies([]);
    localStorage.removeItem('visitedMovies');
  }, []);

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentView('home');
    resetSearch();
  };

  const handleDiscoverClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setCurrentView('discover');
    setIsShowingSearchResults(false);
    try {
      const movies = visitedMovies;
      setDiscoverMovies(movies);
    } catch (error) {
      console.error('Error fetching discover movies:', error);
    }
  };

  return (
    <div className={`App ${isShowingSearchResults ? 'showing-search-results' : ''}`}>
      <header>
        <div className="logo-container">
          <img 
            src={logo} 
            alt="Trailers" 
            className="app-logo" 
            aria-label="Trailers logo" 
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <nav className="main-nav">
          <ul>
            <li>
              <a 
                href="#" 
                onClick={handleHomeClick} 
                className={currentView === 'home' ? 'active' : ''}
              >
                Home
              </a>
            </li>
            <li>
              <a 
                href="#" 
                onClick={handleDiscoverClick} 
                className={currentView === 'discover' ? 'active' : ''}
              >
                Discover
              </a>
            </li>
          </ul>
        </nav>
        <div className="header-right">
          <form onSubmit={searchMovies}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search movies..."
            />
            <button type="submit" className="search-button">
              <svg className="search-icon" viewBox="0 0 24 24">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
            </button>
          </form>
          {user ? (
            <div className="user-menu" ref={dropdownRef}>
              <img src={user.photoURL || undefined} alt="User" className="user-avatar" />
              <span className="user-name" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                {user.displayName}
              </span>
              {isDropdownOpen && (
                <div className="user-dropdown">
                  <button onClick={handleSignOut} className="sign-out-button">Sign Out</button>
                </div>
              )}
            </div>
          ) : (
            <button onClick={handleSignIn} className="sign-in-button">Sign In with Google</button>
          )}
        </div>
      </header>
      
      <div className="main-content">
        {currentView === 'home' && (
          <>
            {!isShowingSearchResults && featuredMovies.length > 0 && (
              <div className="featured-movie-carousel">
                <button className="carousel-arrow left" onClick={goToPreviousMovie}>&lt;</button>
                {featuredMovies.map((movie, index) => (
                  <div 
                    key={movie.id} 
                    className={`featured-movie ${index === carouselState.currentIndex ? 'active' : ''} ${carouselState.transition}`}
                    onDoubleClick={() => handleDoubleClick(index)}
                  >
                    <img 
                      src={`https://image.tmdb.org/t/p/original${movie.backdrop_path}`}
                      alt={movie.title}
                      className={`featured-movie-image ${explodingIndex === index ? 'exploding' : ''}`}
                    />
                    <div className="featured-content">
                      <h2>{movie.title}</h2>
                      <p>{movie.overview}</p>
                      <button onClick={() => playTrailer(movie)}>▶ Play Trailer</button>
                    </div>
                  </div>
                ))}
                <button className="carousel-arrow right" onClick={goToNextMovie}>&gt;</button>
              </div>
            )}
            {isShowingSearchResults ? (
              <div className="movie-row">
                <h2>Search Results</h2>
                <div className="movie-list">
                  {searchResults.map((movie) => (
                    <div key={movie.id} className="movie-tile" onClick={() => openMovieDetail(movie)}>
                      <img 
                        src={`https://image.tmdb.org/t/p/w300${movie.poster_path}`} 
                        alt={movie.title} 
                        loading="lazy"
                      />
                      <div className="movie-rating">
                        <span>{movie.vote_average.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              movieRows.map((row, rowIndex) => (
                <div key={row.title} className="movie-row">
                  <div className="row-header">
                    <h2>{row.title}</h2>
                    {row.title === "Last visited" && visitedMovies.length > 0 && (
                      <button onClick={clearVisitedMovies} className="clear-visited-button">
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="movie-list">
                    {row.movies.map((movie) => (
                      <div key={movie.id} className="movie-tile" onClick={() => openMovieDetail(movie)}>
                        <img 
                          src={`https://image.tmdb.org/t/p/w300${movie.poster_path}`} 
                          alt={movie.title} 
                          loading="lazy"
                        />
                        <div className="movie-rating">
                          <span>{movie.vote_average.toFixed(1)}</span>
                        </div>
                      </div>
                    ))}
                    <div ref={el => observerRefs.current[rowIndex] = el} className="observer-element"></div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
        {currentView === 'discover' && (
          <div className="discover-view">
            <h2>Movies you clicked on</h2>
            <div className="movie-list">
              {discoverMovies.map((movie) => (
                <div key={movie.id} className="movie-tile" onClick={() => openMovieDetail(movie)}>
                  <img 
                    src={`https://image.tmdb.org/t/p/w300${movie.poster_path}`} 
                    alt={movie.title} 
                    loading="lazy"
                  />
                  <div className="movie-rating">
                    <span>{movie.vote_average.toFixed(1)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedMovie && (
        <MovieDetail 
          movie={selectedMovie} 
          onClose={closeMovieDetail}
          onPlayTrailer={() => playTrailer(selectedMovie)}
          watchProviders={watchProviders}
        />
      )}

      {selectedTrailer && (
        <div className="trailer-modal">
          <div className="trailer-header">
            <button className="back-button" onClick={() => setSelectedTrailer(null)}>
              <span>Back to Browse</span>
            </button>
          </div>
          <div className="trailer-content">
            <iframe
              src={`https://www.youtube.com/embed/${selectedTrailer}?autoplay=1`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
